import React, {useState} from "react";
import Box from '@mui/material/Box';
import type {TextFieldProps} from '@mui/material/TextField';
import TextField from '@mui/material/TextField';
import type {GridFilterInputValueProps} from '@mui/x-data-grid';
import {useGridRootProps,} from '@mui/x-data-grid';
import {FilterAlt, Sync} from "@mui/icons-material";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import {Badge, IconButton} from "@mui/material";

const HTML_ID_PREFIX = 'score-between-filter-';

export const ScoreBetweenFilterComponent: React.FC<GridFilterInputValueProps> = ({
                                                                                     item,
                                                                                     applyValue,
                                                                                     focusElementRef = null
                                                                                 }) => {
    // Taken from https://mui.com/x/react-data-grid/filtering/customization/#multiple-values-operator

    const rootProps = useGridRootProps();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const handleClear = () => {
        setFilterValueState([undefined, undefined]);
        setIsDialogOpen(false);
    }
    const handleClose = () => setIsDialogOpen(false);
    const handleSave = () => {
        if (Array.isArray(filterValueState)) {
            const [lowerBound, upperBound] = filterValueState;
            setIsApplying(true);
            filterTimeout.current = setTimeout(() => {
                setIsApplying(false);
                applyValue({...item, value: [lowerBound, upperBound]});
            }, rootProps.filterDebounceMs);
        }
        setIsDialogOpen(false);
    };

    const filterTimeout = React.useRef<ReturnType<typeof setTimeout>>(undefined);
    const [filterValueState, setFilterValueState] = React.useState<[string | undefined, string | undefined]>(item.value ?? [undefined, undefined]);
    const [applying, setIsApplying] = React.useState(false);
    const filterValueStateIsSet = Array.isArray(filterValueState) && filterValueState.length === 2 && (Boolean(filterValueState[0]) || Boolean(filterValueState[1]));

    React.useEffect(() => {
        return () => {
            clearTimeout(filterTimeout.current);
        };
    }, []);

    React.useEffect(() => {
        const itemValue = item.value ?? [undefined, undefined];
        setFilterValueState(itemValue);
    }, [item.value]);

    const updateFilterValue = (lowerBound: string | undefined, upperBound: string | undefined) => {
        clearTimeout(filterTimeout.current);
        setFilterValueState([lowerBound, upperBound]);
    };
    const handleUpperFilterChange: TextFieldProps['onChange'] = (event) => {
        const newUpperBound = event.target.value;
        updateFilterValue(filterValueState[0], String(Number(newUpperBound) / 100));
    };
    const handleLowerFilterChange: TextFieldProps['onChange'] = (event) => {
        const newLowerBound = event.target.value;
        updateFilterValue(String(Number(newLowerBound) / 100), filterValueState[1]);
    };

    const isActive = applying || filterValueStateIsSet;

    return <>
        <Dialog open={isDialogOpen}
                onClose={handleClose}
                aria-labelledby={HTML_ID_PREFIX + "-title"}
                aria-describedby={HTML_ID_PREFIX + "-description"}>
            <DialogTitle id={HTML_ID_PREFIX + "-title"}>
                Filter by score
            </DialogTitle>
            <DialogContent>
                <DialogContentText id={HTML_ID_PREFIX + "-description"}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            flexDirection: 'row',
                            alignItems: 'end',
                            height: 48,
                            pl: '20px',
                        }}
                    >
                        <TextField
                            name="lower-bound-input"
                            placeholder="From"
                            label="From"
                            variant="standard"
                            value={Number(filterValueState[0]) * 100}
                            onChange={handleLowerFilterChange}
                            type="number"
                            inputRef={focusElementRef}
                            sx={{mr: 2}}
                        />
                        <TextField
                            name="upper-bound-input"
                            placeholder="To"
                            label="To"
                            variant="standard"
                            value={Number(filterValueState[1]) * 100}
                            onChange={handleUpperFilterChange}
                            type="number"
                            InputProps={applying ? {endAdornment: <Sync/>} : {}}
                        />
                    </Box>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClear}>Clear</Button>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSave} autoFocus>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
        <IconButton
            size="small"
            onClick={() => setIsDialogOpen(true)}
        >
            <Badge color="secondary" variant="dot" invisible={!isActive}>
                <FilterAlt/>
            </Badge>
        </IconButton>
    </>;

}
